import { Badge, Loader, ThemeIcon } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Spotlight, SpotlightActionData, SpotlightActionGroupData } from '@mantine/spotlight';
import { IconBriefcase, IconBuilding, IconSearch, IconUser } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UserDataType } from '../types';
import { CUSTOMER_SPOTLIGHT_SHORTCUTS } from './customer_spotlight_shortcuts';

const CUSTOMER_SPOTLIGHT_QUERY_BY = 'name_en,name_ar,crmID,civilID,mobile,commercial_register,kfhtrade_id,portfolioKeys,portfolios';
const CUSTOMER_SPOTLIGHT_QUERY_BY_WEIGHTS = '4,4,7,7,6,7,5,8,2';
const MIN_QUERY_LENGTH = 2;

interface CustomerPortfolioSpotlightEntry {
  portfolioTypeCode?: string;
  managerName?: string;
  riskProfile?: string;
  status?: string;
}

interface CustomerSpotlightDocument {
  id?: string;
  documentID?: string;
  crmID?: string;
  civilID?: string;
  mobile?: string;
  commercial_register?: string;
  kfhtrade_id?: string;
  name_en?: string;
  name_ar?: string;
  customerType?: string;
  segment?: string;
  portfolioKeys?: string[];
  portfolios?: Record<string, CustomerPortfolioSpotlightEntry>;
}

interface TypesenseHit<T> {
  document: T;
}

interface TypesenseSearchResult<T> {
  hits?: TypesenseHit<T>[];
  error?: string;
}

interface TypesenseMultiSearchResponse<T> {
  results?: TypesenseSearchResult<T>[];
}

interface CustomerSpotlightProps {
  userData: UserDataType;
}

const normalizeValue = (value: unknown) => `${value ?? ''}`.trim();

const normalizeComparable = (value: unknown) => normalizeValue(value).toLowerCase();

const normalizeDigits = (value: string) => value.replace(/\D/g, '');

const getCustomerDocumentId = (customer: CustomerSpotlightDocument) => customer.documentID || customer.id || '';

const getCustomerDisplayName = (customer: CustomerSpotlightDocument) =>
  customer.name_en || customer.name_ar || customer.crmID || customer.civilID || customer.commercial_register || getCustomerDocumentId(customer);

const getPortfolioKeys = (customer: CustomerSpotlightDocument) => {
  const keys = [
    ...(customer.portfolioKeys || []),
    ...Object.keys(customer.portfolios || {}),
  ];

  return Array.from(new Set(keys.map(normalizeValue).filter(Boolean)));
};

const getMatchedPortfolioKey = (customer: CustomerSpotlightDocument, query: string) => {
  const normalizedQuery = normalizeComparable(query);
  const digitQuery = normalizeDigits(query);
  const portfolioKeys = getPortfolioKeys(customer);

  const exactMatch = portfolioKeys.find((portfolioKey) => normalizeComparable(portfolioKey) === normalizedQuery);
  if (exactMatch) {
    return exactMatch;
  }

  const digitMatch = digitQuery
    ? portfolioKeys.find((portfolioKey) => normalizeDigits(portfolioKey) === digitQuery)
    : undefined;
  if (digitMatch) {
    return digitMatch;
  }

  const partialMatch = portfolioKeys.find((portfolioKey) => normalizeComparable(portfolioKey).includes(normalizedQuery));
  if (partialMatch) {
    return partialMatch;
  }

  return Object.entries(customer.portfolios || {}).find(([, portfolio]) =>
    [
      portfolio.portfolioTypeCode,
      portfolio.managerName,
      portfolio.riskProfile,
      portfolio.status,
    ].some((value) => normalizeComparable(value).includes(normalizedQuery)),
  )?.[0];
};

const getMatchedFields = (customer: CustomerSpotlightDocument, query: string) => {
  const normalizedQuery = normalizeComparable(query);
  const digitQuery = normalizeDigits(query);
  const fields = [
    { label: 'CRM ID', value: customer.crmID },
    { label: 'Civil ID', value: customer.civilID },
    { label: 'Mobile', value: customer.mobile },
    { label: 'Commercial Register', value: customer.commercial_register },
    { label: 'KFH Trade ID', value: customer.kfhtrade_id },
  ];

  return fields.filter(({ value }) => {
    const normalizedValue = normalizeComparable(value);
    const digitValue = normalizeDigits(normalizeValue(value));

    return normalizedValue.includes(normalizedQuery) || (!!digitQuery && digitValue.includes(digitQuery));
  });
};

const buildCustomerDescription = (customer: CustomerSpotlightDocument, query: string, portfolioKey?: string) => {
  const matchedFields = getMatchedFields(customer, query);
  const visibleDetails = matchedFields.length
    ? matchedFields.map(({ label, value }) => `${label}: ${value}`)
    : [
      customer.crmID ? `CRM ID: ${customer.crmID}` : null,
      customer.civilID ? `Civil ID: ${customer.civilID}` : null,
      customer.mobile ? `Mobile: ${customer.mobile}` : null,
      customer.commercial_register ? `CR: ${customer.commercial_register}` : null,
      customer.kfhtrade_id ? `KFH Trade: ${customer.kfhtrade_id}` : null,
    ].filter(Boolean);

  return [
    portfolioKey ? `Portfolio: ${portfolioKey}` : null,
    ...visibleDetails,
  ].filter(Boolean).join(' | ');
};

const buildSearchTexts = (query: string) => {
  const trimmedQuery = query.trim();
  const digitQuery = normalizeDigits(trimmedQuery);

  return Array.from(new Set([
    trimmedQuery,
    digitQuery.length >= MIN_QUERY_LENGTH && digitQuery !== trimmedQuery ? digitQuery : '',
  ].filter(Boolean)));
};

const searchCustomers = async (
  query: string,
  apiKey: string,
  collectionName: string,
  signal: AbortSignal,
) => {
  const searches = buildSearchTexts(query).map((searchText) => ({
    collection: collectionName,
    q: searchText,
    query_by: CUSTOMER_SPOTLIGHT_QUERY_BY,
    query_by_weights: CUSTOMER_SPOTLIGHT_QUERY_BY_WEIGHTS,
    per_page: 8,
    prefix: true,
    num_typos: searchText.length < 5 ? 0 : 1,
  }));

  const response = await fetch(`https://${import.meta.env.VITE_TYPESENSE_SEARCH_HOST}/multi_search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-TYPESENSE-API-KEY': apiKey,
    },
    body: JSON.stringify({ searches }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Customer Spotlight search failed with HTTP ${response.status}`);
  }

  const data = await response.json() as TypesenseMultiSearchResponse<CustomerSpotlightDocument>;
  const seenCustomerIds = new Set<string>();
  const customers: CustomerSpotlightDocument[] = [];

  for (const result of data.results || []) {
    if (result.error) {
      throw new Error(result.error);
    }

    for (const hit of result.hits || []) {
      const customer = hit.document;
      const customerId = getCustomerDocumentId(customer);

      if (!customerId || seenCustomerIds.has(customerId)) {
        continue;
      }

      seenCustomerIds.add(customerId);
      customers.push(customer);
    }
  }

  return customers;
};

export function CustomerSpotlight({ userData }: CustomerSpotlightProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(query, 250);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<CustomerSpotlightDocument[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  const customerSearchKey = userData.customerCollectionSearchKey;
  const customerCollectionName = import.meta.env.VITE_TYPESENSE_CUSTOMERS_COLLECTION;
  const trimmedQuery = debouncedQuery.trim();

  useEffect(() => {
    if (!customerSearchKey || trimmedQuery.length < MIN_QUERY_LENGTH) {
      setCustomers([]);
      setSearchError(null);
      setLoading(false);
      return;
    }

    const abortController = new AbortController();
    let ignore = false;

    setLoading(true);
    setSearchError(null);

    searchCustomers(trimmedQuery, customerSearchKey, customerCollectionName, abortController.signal)
      .then((results) => {
        if (!ignore) {
          setCustomers(results);
        }
      })
      .catch((error) => {
        if (!ignore && error.name !== 'AbortError') {
          setCustomers([]);
          setSearchError(error instanceof Error ? error.message : 'Customer Spotlight search failed');
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [customerCollectionName, customerSearchKey, trimmedQuery]);

  const actions = useMemo<SpotlightActionData[]>(() => customers.map((customer) => {
    const customerId = getCustomerDocumentId(customer);
    const portfolioKey = getMatchedPortfolioKey(customer, trimmedQuery);
    const title = getCustomerDisplayName(customer);
    const route = portfolioKey
      ? `/customers/${encodeURIComponent(customerId)}?tab=positions&portfolio=${encodeURIComponent(portfolioKey)}`
      : `/customers/${encodeURIComponent(customerId)}`;

    return {
      id: portfolioKey ? `customer-${customerId}-portfolio-${portfolioKey}` : `customer-${customerId}`,
      label: portfolioKey ? `${portfolioKey} - ${title}` : title,
      description: buildCustomerDescription(customer, trimmedQuery, portfolioKey),
      leftSection: (
        <ThemeIcon variant="light" color={portfolioKey ? 'grape' : customer.customerType === 'company' ? 'orange' : 'blue'}>
          {portfolioKey ? <IconBriefcase size={16} /> : customer.customerType === 'company' ? <IconBuilding size={16} /> : <IconUser size={16} />}
        </ThemeIcon>
      ),
      rightSection: (
        <Badge variant="light" color={portfolioKey ? 'grape' : 'blue'}>
          {portfolioKey ? 'Portfolio' : 'Customer'}
        </Badge>
      ),
      keywords: [
        customer.crmID,
        customer.name_en,
        customer.name_ar,
        customer.civilID,
        customer.mobile,
        customer.commercial_register,
        customer.kfhtrade_id,
        ...getPortfolioKeys(customer),
      ].filter(Boolean) as string[],
      onClick: () => navigate(route),
    };
  }), [customers, navigate, trimmedQuery]);

  const groupedActions = useMemo<SpotlightActionGroupData[]>(() => actions.length
    ? [{ group: 'Customers', actions }]
    : [], [actions]);

  const nothingFound = useMemo(() => {
    if (!customerSearchKey) {
      return 'Customer search is not ready';
    }

    if (query.trim().length < MIN_QUERY_LENGTH) {
      return 'Type at least 2 characters';
    }

    if (loading) {
      return 'Searching customers...';
    }

    return searchError || 'No matching customers';
  }, [customerSearchKey, loading, query, searchError]);

  return (
    <Spotlight
      actions={groupedActions}
      filter={(_, spotlightActions) => spotlightActions}
      highlightQuery
      limit={10}
      nothingFound={nothingFound}
      onQueryChange={setQuery}
      query={query}
      searchProps={{
        placeholder: 'Search customers by name, CRM ID, civil ID, phone, CR, KFH Trade ID, or portfolio...',
        leftSection: <IconSearch size={18} />,
        rightSection: loading ? <Loader size="xs" /> : undefined,
      }}
      shortcut={CUSTOMER_SPOTLIGHT_SHORTCUTS}
    />
  );
}
